import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useOptionalAdminStats } from '@/contexts/AdminStatsContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { User, LogOut, Settings, Circle, TrendingUp, Briefcase, Lightbulb, GraduationCap, Globe, FileText, Handshake, Brain } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface MemberData {
  name: string;
  status: string;
  subscription_type: string;
  join_date: string;
  image?: string;
}

export const UserStatusIndicator: React.FC = React.memo(() => {
  const { user, member, logout, isAdmin, subscription } = useAuth();
  const { engagementRate, loading: statsLoading } = useOptionalAdminStats();
  const navigate = useNavigate();

  if (!user) return null;

  const getUserName = () => {
    // Debug logging
    console.log('🔍 UserStatusIndicator - getUserName debug:', {
      'member?.name': member?.name,
      'user?.email': user?.email,
      'member object': member
    });
    
    // Use the name field from user_complete_profile view
    if (member?.name && member.name.trim() !== '') {
      return member.name;
    }
    
    // Fallback to email
    return user?.email?.split('@')[0] || 'Utilisateur';
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getRoleBadgeVariant = () => {
    return isAdmin ? 'destructive' : 'secondary';
  };

  const getRoleText = () => {
    return isAdmin ? 'Admin' : 'Membre';
  };

  const getStatusColor = () => {
    if (member?.status === 'actif' || subscription?.subscribed) {
      return 'text-green-500';
    }
    return 'text-red-500';
  };

  const isAccountUpToDate = () => {
    return member?.status === 'actif' || subscription?.subscribed;
  };

  const userName = getUserName();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-auto p-2 hover:bg-muted">
          <div className="flex items-center gap-3">
            <div className="relative">
               <Avatar className="h-8 w-8">
                 <AvatarImage src={member?.image || undefined} />
                 <AvatarFallback className="text-xs bg-singu-yellow text-singu-dark">
                   {getInitials(userName)}
                 </AvatarFallback>
               </Avatar>
              <Circle 
                className={`absolute -bottom-1 -right-1 h-3 w-3 fill-current ${getStatusColor()}`}
              />
            </div>
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-2">
                 <span className="text-sm font-medium text-white">
                   {userName}
                 </span>
                <Badge variant={getRoleBadgeVariant()} className="text-xs px-1 py-0">
                  {getRoleText()}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                 <span className="text-xs text-white">
                   {isAccountUpToDate() ? 'À jour' : 'Expiré'}
                 </span>
                {isAdmin && !statsLoading && (
                  <div className="flex items-center gap-1">
                     <TrendingUp className="h-3 w-3 text-singu-yellow" />
                     <span className="text-xs text-singu-yellow font-medium">
                       {engagementRate}%
                     </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={() => navigate('/dashboard')}>
          <User className="mr-2 h-4 w-4" />
          <span>Tableau de bord</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/formation')}>
          <GraduationCap className="mr-2 h-4 w-4" />
          <span>Formation</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/business')}>
          <Briefcase className="mr-2 h-4 w-4" />
          <span>Business</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/international')}>
          <Globe className="mr-2 h-4 w-4" />
          <span>International</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/deals')}>
          <Handshake className="mr-2 h-4 w-4" />
          <span>Deals & Partenariats</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profile')}>
          <User className="mr-2 h-4 w-4" />
          <span>Mon Profil</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/subscription')}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Mon Abonnement</span>
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem onClick={() => navigate('/admin')}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Administration</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="text-red-600">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Déconnexion</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});